import type { Appointment } from '@/types/appointment';

interface CalendarEventData {
  title: string;
  starts_at: string;
  ends_at: string;
  meeting_type: 'in_person' | 'video' | 'phone';
  meeting_link?: string | null;
  notes?: string | null;
  client: {
    name: string;
    phone: string;
    status?: 'lead' | 'active' | 'past' | 'cold' | null;
  };
}

export class GoogleCalendarLinkService {
  private static readonly TIMEZONE = 'Asia/Kolkata'; // IST
  private static readonly GOOGLE_CALENDAR_BASE_URL = 'https://calendar.google.com/calendar/render';

  /**
   * Generates a pre-filled Google Calendar URL for an appointment
   * Opens in user's browser with all appointment details
   */
  static generateAddToCalendarUrl(
    appointment: Appointment,
    clientName: string,
    clientPhone: string,
    clientStatus?: 'lead' | 'active' | 'past' | 'cold' | null
  ): string {
    const eventData: CalendarEventData = {
      title: appointment.title,
      starts_at: appointment.starts_at,
      ends_at: appointment.ends_at,
      meeting_type: appointment.meeting_type,
      meeting_link: appointment.meeting_link,
      notes: appointment.notes,
      client: {
        name: clientName,
        phone: clientPhone,
        status: clientStatus,
      },
    };

    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: this.formatEventTitle(eventData),
      dates: this.formatDateRange(eventData.starts_at, eventData.ends_at),
      details: this.formatEventDescription(eventData),
      location: this.formatLocation(eventData),
      ctz: this.TIMEZONE,
    });

    return `${this.GOOGLE_CALENDAR_BASE_URL}?${params.toString()}`;
  }

  /**
   * Formats event title as: "Client Name - Appointment Title"
   * Example: "Sarah Johnson - Initial Consultation"
   */
  private static formatEventTitle(eventData: CalendarEventData): string {
    return `${eventData.client.name} - ${eventData.title}`;
  }

  /**
   * Formats date range for Google Calendar
   * Format: YYYYMMDDTHHmmss/YYYYMMDDTHHmmss (in UTC)
   */
  private static formatDateRange(startTime: string, endTime: string): string {
    const formatForGoogle = (dateString: string): string => {
      const date = new Date(dateString);
      return date
        .toISOString()
        .replace(/[-:]/g, '')
        .replace(/\.\d{3}Z/, 'Z');
    };

    return `${formatForGoogle(startTime)}/${formatForGoogle(endTime)}`;
  }

  /**
   * Formats event description with client details
   * Includes: Client name, phone, status, meeting type, meeting link
   */
  private static formatEventDescription(eventData: CalendarEventData): string {
    const lines: string[] = [];

    // Add appointment notes if present
    if (eventData.notes?.trim()) {
      lines.push(eventData.notes.trim());
      lines.push(''); // Empty line separator
    }

    // Client information
    lines.push(`Client: ${eventData.client.name}`);
    lines.push(`Phone: ${eventData.client.phone}`);

    // Client status (formatted)
    if (eventData.client.status) {
      const statusLabel = this.formatClientStatus(eventData.client.status);
      lines.push(`Status: ${statusLabel}`);
    }

    // Meeting type
    lines.push(`Type: ${this.formatMeetingType(eventData.meeting_type)}`);

    // Meeting link (only if video call and link exists)
    if (eventData.meeting_type === 'video' && eventData.meeting_link?.trim()) {
      lines.push(`Meeting Link: ${eventData.meeting_link.trim()}`);
    }

    return lines.join('\n');
  }

  /**
   * Formats location field based on meeting type
   * For video calls with links, use the link as location
   */
  private static formatLocation(eventData: CalendarEventData): string {
    switch (eventData.meeting_type) {
      case 'video':
        return eventData.meeting_link?.trim() || 'Video Call';
      case 'phone':
        return 'Phone Call';
      case 'in_person':
        return 'In Person';
      default:
        return '';
    }
  }

  /**
   * Formats meeting type for display
   */
  private static formatMeetingType(meetingType: 'in_person' | 'video' | 'phone'): string {
    const typeMap = {
      in_person: 'In Person',
      video: 'Video Call',
      phone: 'Phone Call',
    };
    return typeMap[meetingType];
  }

  /**
   * Formats client status for display
   */
  private static formatClientStatus(status: 'lead' | 'active' | 'past' | 'cold'): string {
    const statusMap = {
      lead: 'Lead',
      active: 'Active Client',
      past: 'Past Client',
      cold: 'Cold Lead',
    };
    return statusMap[status];
  }
}
